import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "./../../hooks/auth";
import { Link, useHistory } from "react-router-dom";
import OutsideClickHandler from "react-outside-click-handler";
import {
  Container,
  Content,
  RoutesContainer,
  MenuButton,
  OptionButton,
  OptionsToRouteContainer,
  MoreOptionsOptionsButton,
  MoreOptionsOptionsContainer,
} from "./styles";
import logoImg from "./../../assets/components/Header/logoTopo.svg";
import { ChevronDown, ChevronRight, Menu } from "lucide-react";
import ModalPropertiesPerConsultant from "../ModalPropertiesPerConsultant";
import ModalPropertiesPerNeighborhood from "../ModalPropertiesPerNeighborhood";
import MenuToNavigate from "../MenuToNavigate";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import { useToast } from "../../hooks/toast";
import ModalVisitsPerConsultants from "../ModalVisitsPerConsultants";
import ModalVisitsPerOwner from "../ModalVisitsPerOwner";
import ModalVisitsPerProperty from "../ModalVisitsPerProperty";
import ModalActivePropertiesPerPeriod from "../ModalActivePropertiesPerPeriod";
import ModalRentReport from "../ModalRentReport";
import { OptionTypes } from "./OptionTypes";
import { PERM, PERMISSIONS_FOR_MORE_MENU } from "../../utils/permissions";
import { useScheduleTeam } from "../../hooks/scheduleTeam";
import Select, { components } from "react-select";

interface IHeader {
  isLoginScreen?: boolean;
}

interface IBoard {
  id: number;
  name: string;
}

const TeamDropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <ChevronDown className="h-4 w-4 text-[#666666]" aria-hidden />
  </components.DropdownIndicator>
);

const Header: React.FC<IHeader> = ({ isLoginScreen }: IHeader) => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user, access_token, dateToExpires, signOut, hasPermission, hasAnyPermission } = useAuth();

  const [openMenu, setOpenMenu] = useState(false);
  const [openOptionsToMoreOptions, setOpenOptionsToMoreOptions] = useState(false);

  const [selectedOption, setSelectedOption] = useState(OptionTypes.NONE);

  const [openModalPropertiesPerConsultant, setOpenModalPropertiesPerConsultant] = useState(false);
  const [openModalPropertiesPerNeighborhood, setOpenModalPropertiesPerNeighborhood] = useState(false);
  const [openModalVisitsPerConsultant, setOpenModalVisitsPerConsultant] = useState(false);
  const [openModalVisitsPerOwner, setOpenModalVisitsPerOwner] = useState(false);
  const [openModalVisitsPerProperty, setOpenModalVisitsPerProperty] = useState(false);
  const [openModalRentReport, setOpenModalRentReport] = useState(false);
  const [openModalActivePropertiesPerPeriod, setOpenModalActivePropertiesPerPeriod] = useState(false);

  const [offers, setOffers] = useState<any[]>([]);
  const [boards, setBoards] = useState<IBoard[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);

  const hasUnreadOffers = offers.length > 0;

  const isPhotographer = user?.role === "photographer";
  const isPartnerBroker = user?.role === "partner_broker";
  const canBoards = hasAnyPermission(["adm.boards.index", "adm.boards.show"]);
  const canProperties = hasAnyPermission([
    PERM.propertyList,
    PERM.propertyCreate,
    PERM.propertyShow,
    PERM.propertyUpdate,
    PERM.propertyPartner,
  ]);
  const canReports = hasAnyPermission([
    PERM.reportConsultant,
    PERM.reportNeighborhood,
    PERM.reportVisits,
    PERM.reportVisitsByOwner,
    PERM.reportVisitsByProperty,
    PERM.reportRents,
    PERM.reportActives,
  ]);
  const canFAQ = hasAnyPermission([PERM.faqList, PERM.faqCreate]);
  const canRentMenu = hasPermission(PERM.offerList) || hasPermission(PERM.documents);
  const canContractsMenu = hasAnyPermission([PERM.contractsModels, PERM.contractsSend]);
  const canUsersMenu = hasAnyPermission([PERM.usersList, PERM.usersCreate, PERM.bulkConsultants]);
  const canMoreMenu =
    isPartnerBroker || hasAnyPermission([...PERMISSIONS_FOR_MORE_MENU]);
  const canSeeUnreadOffers =
    hasPermission(PERM.offerUnread) ||
    (isPartnerBroker && hasPermission(PERM.offerList));

  const scheduleTeam = useScheduleTeam();

  useEffect(() => {
    async function loadApi() {
      try {
        if (canSeeUnreadOffers) {
          const token = await refreshToken(dateToExpires, access_token);

          api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

          if (window.location.pathname.includes("/see-offer/")) {
            setTimeout(async () => {
              const response = await api.get("/api/adm/offers/unread/get");
              setOffers(response.data.data ?? []);
            }, 2000);
          } else {
            const response = await api.get("/api/adm/offers/unread/get");
            setOffers(response.data.data ?? []);
          }
        } else {
          setOffers([]);
        }
      } catch (err: any) {
        console.log(err);

        if (err?.response?.data?.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }
      }
    }

    if (user || isLoginScreen) {
      loadApi();
    } else {
      history.push("/");
    }
  }, [user, history, dateToExpires, access_token, addToast, isLoginScreen, canSeeUnreadOffers]);

  async function loadBoards() {
    if (!canBoards) return;
    try {
      setLoadingBoards(true);
      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const response = await api.get("/api/adm/boards");
      setBoards(response.data.data ?? []);
    } catch (err) {
      console.error("Erro ao carregar boards:", err);
      addToast({
        title: "Erro ao carregar os boards",
        type: "error",
      });
    } finally {
      setLoadingBoards(false);
    }
  }

  const formatUserName = useMemo(() => {
    if (user) {
      const indexOfSpace = user.first_name.indexOf(" ");
      if (indexOfSpace < 0) return user.first_name;
      return user.first_name.substring(0, indexOfSpace);
    }
  }, [user]);

  const teamOptions = useMemo(
    () =>
      scheduleTeam.myTeams.map((team) => ({
        label: team.name,
        value: team.id,
      })),
    [scheduleTeam.myTeams]
  );

  const selectedTeamOption = useMemo(
    () => teamOptions.find((team) => team.value === scheduleTeam.selectedTeamId) ?? null,
    [teamOptions, scheduleTeam.selectedTeamId]
  );

  const handleSignOut = useCallback(async () => {
    await signOut();
    history.push("/");
  }, [signOut, history]);

  function unselectOption() {
    setSelectedOption(OptionTypes.NONE);
  }

  function closeDropdownsOnMouseLeave() {
    setSelectedOption(OptionTypes.NONE);
    setOpenOptionsToMoreOptions(false);
  }

  function handleOpenBoardOption() {
    setSelectedOption(OptionTypes.KANBAN);
    if (!loadingBoards && boards.length === 0) loadBoards();
  }

  function handleOpenRentOption() {
    setSelectedOption(OptionTypes.RENT);
  }

  const homeRoute = isLoginScreen
    ? "/"
    : isPhotographer
      ? "/photographers-schedule"
      : "/schedules";

  return (
    <Container>
      <Content>
        <Link to={homeRoute}>
          <img src={logoImg} alt="logo" />
        </Link>

        {!!openModalPropertiesPerConsultant && (
          <ModalPropertiesPerConsultant
            open={openModalPropertiesPerConsultant}
            functionToCloseModal={() => setOpenModalPropertiesPerConsultant(false)}
          />
        )}

        {!!openModalPropertiesPerNeighborhood && (
          <ModalPropertiesPerNeighborhood
            open={openModalPropertiesPerNeighborhood}
            functionToCloseModal={() => setOpenModalPropertiesPerNeighborhood(false)}
          />
        )}

        {!!openModalVisitsPerConsultant && (
          <ModalVisitsPerConsultants
            open={openModalVisitsPerConsultant}
            functionToCloseModal={() => setOpenModalVisitsPerConsultant(false)}
          />
        )}

        {!!openModalVisitsPerOwner && (
          <ModalVisitsPerOwner
            open={openModalVisitsPerOwner}
            functionToCloseModal={() => setOpenModalVisitsPerOwner(false)}
          />
        )}

        {!!openModalVisitsPerProperty && (
          <ModalVisitsPerProperty
            open={openModalVisitsPerProperty}
            functionToCloseModal={() => setOpenModalVisitsPerProperty(false)}
          />
        )}

        {!!openModalRentReport && (
          <ModalRentReport
            open={openModalRentReport}
            functionToCloseModal={() => setOpenModalRentReport(false)}
          />
        )}

        {openModalActivePropertiesPerPeriod && (
          <ModalActivePropertiesPerPeriod
            open={openModalActivePropertiesPerPeriod}
            functionToCloseModal={() => setOpenModalActivePropertiesPerPeriod(false)}
          />
        )}

        {!isLoginScreen && (
          <>
            <RoutesContainer>
              {scheduleTeam.usesTeamSchedules &&
                scheduleTeam.myTeams.length > 0 &&
                scheduleTeam.selectedTeamId != null && (
                  <div className="mr-2 shrink-0">
                    <Select
                      aria-label="Time na agenda"
                      value={selectedTeamOption}
                      options={teamOptions}
                      isSearchable={false}
                      components={{
                        DropdownIndicator: TeamDropdownIndicator,
                        IndicatorSeparator: () => null,
                      }}
                      onChange={(option) => scheduleTeam.setSelectedTeamId(option?.value ?? null)}
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "auto",
                        }),
                        control: (base, state) => ({
                          ...base,
                          minHeight: "unset",
                          border: 0,
                          boxShadow: "none",
                          backgroundColor: "transparent",
                          borderRadius: 0,
                          cursor: "pointer",
                          padding: "0",
                          width: "auto",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          margin: 0,
                          maxWidth: "100%",
                          color: "#dd6435",
                          fontSize: "16px",
                          fontWeight: 400,
                        }),
                        input: (base) => ({
                          ...base,
                          margin: 0,
                          padding: 0,
                        }),
                        placeholder: (base) => ({
                          ...base,
                          margin: 0,
                          color: "#dd6435",
                          fontSize: "15px",
                        }),
                        indicatorsContainer: (base) => ({
                          ...base,
                          padding: 0,
                          marginLeft: "4px",
                          width: "auto",
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          padding: 0,
                          color: "#dd6435",
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 20,
                          minWidth: "180px",
                        }),
                        option: (base, state) => ({
                          ...base,
                          fontSize: "15px",
                          color: "#666666",
                          backgroundColor: state.isFocused ? "#f5f5f5" : "#fff",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>
                )}

              {canBoards && (
                <OptionButton
                  onClick={handleOpenBoardOption}
                  onMouseEnter={handleOpenBoardOption}
                  onMouseLeave={closeDropdownsOnMouseLeave}
                >
                  <p>Boards</p>
                  <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                  {selectedOption === OptionTypes.KANBAN && (
                    <OutsideClickHandler onOutsideClick={unselectOption}>
                      <OptionsToRouteContainer style={{ maxHeight: "300px", overflowY: "auto" }}>
                        {loadingBoards ? (
                          <p>Carregando boards...</p>
                        ) : (
                          boards.map((board) => (
                            <Link
                              key={board.id}
                              to={`/kanban/${board.id}`}
                              onClick={() => setSelectedOption(OptionTypes.NONE)}
                              className="board-link"
                            >
                              {board.name}
                            </Link>
                          ))
                        )}
                      </OptionsToRouteContainer>
                    </OutsideClickHandler>
                  )}
                </OptionButton>
              )}

              {canProperties && (
                <OptionButton
                  onClick={() => setSelectedOption(OptionTypes.PROPERTIES)}
                  onMouseEnter={() => setSelectedOption(OptionTypes.PROPERTIES)}
                  onMouseLeave={closeDropdownsOnMouseLeave}
                >
                  <p>Imóveis</p>
                  <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                  {selectedOption === OptionTypes.PROPERTIES && (
                    <OutsideClickHandler onOutsideClick={unselectOption}>
                      <OptionsToRouteContainer>
                        {hasPermission(PERM.propertyList) && <Link to="/properties/1">Visualizar</Link>}
                        {hasPermission(PERM.propertyCreate) && <Link to="/create-property">Cadastrar</Link>}
                      </OptionsToRouteContainer>
                    </OutsideClickHandler>
                  )}
                </OptionButton>
              )}

              {isPartnerBroker && (
                <OptionButton>
                  <Link to="/my-properties/1">Meus imóveis</Link>
                </OptionButton>
              )}

              {canReports && (
                <OptionButton
                  onClick={() => setSelectedOption(OptionTypes.REPORTS)}
                  onMouseEnter={() => setSelectedOption(OptionTypes.REPORTS)}
                  onMouseLeave={closeDropdownsOnMouseLeave}
                >
                  <p>Relatórios</p>
                  <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                  {selectedOption === OptionTypes.REPORTS && (
                    <OutsideClickHandler onOutsideClick={unselectOption}>
                      <OptionsToRouteContainer>
                        {hasPermission(PERM.reportConsultant) && (
                          <button
                            className="report-modal-btn"
                            onClick={() => setOpenModalPropertiesPerConsultant(true)}
                          >
                            Imóveis por consultor
                          </button>
                        )}
                        {hasPermission(PERM.reportNeighborhood) && (
                          <button
                            className="report-modal-btn"
                            onClick={() => setOpenModalPropertiesPerNeighborhood(true)}
                          >
                            Imóveis por bairro
                          </button>
                        )}
                        {hasPermission(PERM.reportVisits) && (
                          <button
                            className="report-modal-btn"
                            onClick={() => setOpenModalVisitsPerConsultant(true)}
                          >
                            Visitas por consultor
                          </button>
                        )}
                        {hasPermission(PERM.reportVisitsByOwner) && (
                          <button
                            className="report-modal-btn"
                            onClick={() => setOpenModalVisitsPerOwner(true)}
                          >
                            Visitas por Proprietário
                          </button>
                        )}
                        {hasPermission(PERM.reportVisitsByProperty) && (
                          <button
                            className="report-modal-btn"
                            onClick={() => setOpenModalVisitsPerProperty(true)}
                          >
                            Visitas por Imóvel
                          </button>
                        )}
                        {hasPermission(PERM.reportRents) && (
                          <button
                            className="report-modal-btn"
                            onClick={() => setOpenModalRentReport(true)}
                          >
                            Fechamentos
                          </button>
                        )}
                        {hasPermission(PERM.reportActives) && (
                          <button
                            className="report-modal-btn"
                            onClick={() => setOpenModalActivePropertiesPerPeriod(true)}
                          >
                            Imóveis Ativos por Período
                          </button>
                        )}
                      </OptionsToRouteContainer>
                    </OutsideClickHandler>
                  )}
                </OptionButton>
              )}

              {canRentMenu && (
                <OptionButton
                  onClick={handleOpenRentOption}
                  onMouseEnter={handleOpenRentOption}
                  onMouseLeave={closeDropdownsOnMouseLeave}
                >
                  <p>Locação</p>
                  {hasUnreadOffers && <span className="notification-dot" />}
                  <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                  {selectedOption === OptionTypes.RENT && (
                    <OutsideClickHandler onOutsideClick={unselectOption}>
                      <OptionsToRouteContainer>
                        {hasPermission(PERM.offerList) && (
                          <Link to="/offers/1" onClick={unselectOption}>
                            Propostas
                            {hasUnreadOffers && <span className="offers-count">{offers.length}</span>}
                          </Link>
                        )}
                        {hasPermission(PERM.documents) && (
                          <Link to="/documents" onClick={unselectOption}>
                            Documentação
                          </Link>
                        )}
                      </OptionsToRouteContainer>
                    </OutsideClickHandler>
                  )}
                </OptionButton>
              )}

              {/* Contratos - usando permissões */}
              {canContractsMenu && (
                <OptionButton
                  onClick={() => setSelectedOption(OptionTypes.CONTRACTS)}
                  onMouseEnter={() => setSelectedOption(OptionTypes.CONTRACTS)}
                  onMouseLeave={closeDropdownsOnMouseLeave}
                >
                  <p>Contratos</p>
                  <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                  {selectedOption === OptionTypes.CONTRACTS && (
                    <OutsideClickHandler onOutsideClick={unselectOption}>
                      <OptionsToRouteContainer>
                        {hasPermission(PERM.contractsModels) && (
                          <Link to="/show-contract-templates">Modelos</Link>
                        )}
                        {hasPermission(PERM.contractsSend) && (
                          <Link to="/send-contract-to-signers">Enviar para signatários</Link>
                        )}
                      </OptionsToRouteContainer>
                    </OutsideClickHandler>
                  )}
                </OptionButton>
              )}

              {canMoreMenu && (
                <OptionButton
                  onClick={() => setOpenOptionsToMoreOptions(true)}
                  onMouseEnter={() => setOpenOptionsToMoreOptions(true)}
                  onMouseLeave={closeDropdownsOnMouseLeave}
                >
                  <p>Mais</p>
                  <ChevronDown className="h-4 w-4 text-current" aria-hidden />

                  {openOptionsToMoreOptions && (
                    <OutsideClickHandler onOutsideClick={() => setOpenOptionsToMoreOptions(false)}>
                      <OptionsToRouteContainer left="auto" right="0">
                        {hasPermission(PERM.photoSchedule) && (
                          <Link
                            to="/photographers-schedule"
                            className="long-link"
                            onClick={() => setOpenOptionsToMoreOptions(false)}
                          >
                            Agenda Fotógrafos
                          </Link>
                        )}

                        {hasPermission(PERM.clientsWithoutContract) && (
                          <Link to="/clients-without-contract" className="long-link">
                            Clientes Sem Contrato
                          </Link>
                        )}

                        {hasPermission(PERM.faqList) && <Link to="/list-faqs/1">FAQS</Link>}

                        {hasAnyPermission([PERM.notificationsLogs, PERM.notificationsAutomations]) && (
                          <Link
                            to="/notifications/1"
                            onClick={() => setOpenOptionsToMoreOptions(false)}
                          >
                            Notificações
                          </Link>
                        )}

                        {/* Restrictive analysis menu entry temporarily hidden (header). */}
                        {/*
                        <Link to="/analise-restritiva" onClick={() => setOpenOptionsToMoreOptions(false)}>
                          Análise Restritiva
                        </Link>
                        */}

                        {hasPermission(PERM.styleGuide) && (
                          <Link
                            to="/style-guide"
                            className="long-link"
                            onClick={() => setOpenOptionsToMoreOptions(false)}
                          >
                            Guia de estilo
                          </Link>
                        )}

                        <MoreOptionsOptionsContainer>
                          {hasAnyPermission([PERM.commercialList, PERM.commercialCreate]) && (
                            <MoreOptionsOptionsButton
                              onClick={() => setSelectedOption(OptionTypes.COMMERCIAL_REGIONS)}
                              onMouseEnter={() => setSelectedOption(OptionTypes.COMMERCIAL_REGIONS)}
                            >
                              <p>Regiões Comerciais</p>
                              <ChevronRight className="h-4 w-4 text-current" aria-hidden />

                              {selectedOption === OptionTypes.COMMERCIAL_REGIONS && (
                                <OutsideClickHandler onOutsideClick={() => setSelectedOption(OptionTypes.NONE)}>
                                  <OptionsToRouteContainer top="0" left="-100%">
                                    {hasPermission(PERM.commercialList) && (
                                      <Link to="/show-commercial-regions">Visualizar</Link>
                                    )}
                                    {hasPermission(PERM.commercialCreate) && (
                                      <Link to="/create-commercial-region">Cadastrar</Link>
                                    )}
                                  </OptionsToRouteContainer>
                                </OutsideClickHandler>
                              )}
                            </MoreOptionsOptionsButton>
                          )}

                          {hasAnyPermission([PERM.guaranteesList, PERM.guaranteesCreate]) && (
                            <MoreOptionsOptionsButton
                              onClick={() => setSelectedOption(OptionTypes.GUARANTEES)}
                              onMouseEnter={() => setSelectedOption(OptionTypes.GUARANTEES)}
                            >
                              <p>Garantias</p>
                              <ChevronRight className="h-4 w-4 text-current" aria-hidden />

                              {selectedOption === OptionTypes.GUARANTEES && (
                                <OutsideClickHandler onOutsideClick={() => setSelectedOption(OptionTypes.NONE)}>
                                  <OptionsToRouteContainer top="0" left="-100%">
                                    {hasPermission(PERM.guaranteesList) && <Link to="/show-guarantees">Visualizar</Link>}
                                    {hasPermission(PERM.guaranteesCreate) && <Link to="/create-guarantee">Cadastrar</Link>}
                                  </OptionsToRouteContainer>
                                </OutsideClickHandler>
                              )}
                            </MoreOptionsOptionsButton>
                          )}

                          {canUsersMenu && (
                            <MoreOptionsOptionsButton onClick={() => setSelectedOption(OptionTypes.CONSULTANTS)} onMouseEnter={() => setSelectedOption(OptionTypes.CONSULTANTS)}>
                              <p>Usuários</p>
                              <ChevronRight className="h-4 w-4 text-current" aria-hidden />

                              {selectedOption === OptionTypes.CONSULTANTS && (
                                <OutsideClickHandler onOutsideClick={() => setSelectedOption(OptionTypes.NONE)}>
                                  <OptionsToRouteContainer top="0" left="-100%">
                                    {hasPermission(PERM.usersList) && <Link to="/users">Visualizar</Link>}
                                    {hasPermission(PERM.usersCreate) && <Link to="/create-user">Cadastrar</Link>}
                                    {hasPermission(PERM.bulkConsultants) && (
                                      <Link to="/consultants/update">Consultores em Massa</Link>
                                    )}
                                  </OptionsToRouteContainer>
                                </OutsideClickHandler>
                              )}
                            </MoreOptionsOptionsButton>
                          )}
                        </MoreOptionsOptionsContainer>
                      </OptionsToRouteContainer>
                    </OutsideClickHandler>
                  )}
                </OptionButton>
              )}

              {!!user && (
                <Link to="/my-account" className="username">
                  {formatUserName}
                </Link>
              )}

              <button className="sign-out-button" onClick={handleSignOut}>
                <p style={{ color: "#dd6435" }}>Sair</p>
              </button>
            </RoutesContainer>

            <MenuButton onClick={() => setOpenMenu(!openMenu)}>
              <Menu className="h-[26px] w-[26px] text-[#dd6435]" aria-hidden />
            </MenuButton>

            {!!openMenu && (
              <MenuToNavigate open={openMenu} functionToCloseModal={() => setOpenMenu(false)} />
            )}
          </>
        )}
      </Content>
    </Container>
  );
};

export default Header;
